// schedule-view.js — CLIENT-SIDE shaping, raw FHIR -> the buildScheduleView
// DTO (server/opd/view-adapter.mjs:2412), computed from a 7-day Appointment
// search + the same-range Encounter search (joined by patient+start — same
// fallback the server itself uses when an Appointment has no linked
// Encounter id). Appointment.start is a real UTC instant; day buckets and the
// printed HH:mm are computed in the CLINIC timezone (clinic-time.js), matching
// the server shaper — never string-slice the ISO (that prints UTC, not IST).
//
// The old scope-clamping (H11 in buildScheduleView — "a doctor can't widen
// their own board via ?scope=all") is NOT ported: RLS enforces "this doctor's
// own department/participation" at the database layer, so there is nothing
// left to clamp app-side (same reasoning as patients-view.js's dropped
// chartGated re-check).
import { formatClinicTime, clinicDateKey } from '../clinic-time.js';
import { ageFromBirthDate, nameOf, initials } from '../fhir-person.js';

const OPD_STATE = 'https://healther.io/opd/state';
const MRN_SYS = 'https://healther.io/opd/mrn';
const APPT_STATUS_TO_STATE = { booked: 'DRAFT', arrived: 'READY', 'checked-in': 'IN_CONSULT', fulfilled: 'COMPLETED', cancelled: 'DRAFT', noshow: 'DRAFT' };

const ext = (res, url) => ((res && res.extension) || []).find((e) => e.url === url);
const fmtTime = (iso) => formatClinicTime(iso);
const dayLabel = (key, todayKey, tomorrowKey) => {
  if (key === todayKey) return 'Today';
  if (key === tomorrowKey) return 'Tomorrow';
  try { return new Date(key + 'T00:00:00Z').toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' }); } catch { return key; }
};

export function groupAppointmentBundle(bundle) {
  const patients = {}; const appts = [];
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry.resource; if (!r) continue;
    if (r.resourceType === 'Patient') patients['Patient/' + r.id] = r;
    else if (r.resourceType === 'Appointment') appts.push(r);
  }
  return { patients, appts };
}

function patientRefOf(appt) {
  const p = (appt.participant || []).find((pp) => pp.actor && pp.actor.reference && pp.actor.reference.startsWith('Patient/'));
  return p ? p.actor.reference : null;
}
function doctorParticipantOf(appt) {
  return (appt.participant || []).find((pp) => pp.actor && pp.actor.reference && pp.actor.reference.startsWith('Practitioner/'));
}

// Builds the { today, days, totalAppointments } DTO from raw Appointments +
// same-range Encounters (for live state/encounterId — matched by patient+start).
export function buildScheduleView({ appts, patients, encs, todayKey, dayCount = 7 }) {
  const encByKey = new Map();
  for (const enc of encs || []) {
    const key = (enc.subject && enc.subject.reference || '') + '|' + ((enc.period && enc.period.start) || '');
    encByKey.set(key, enc);
  }

  const dayKeys = Array.from({ length: dayCount }, (_, i) => {
    // todayKey is a clinic-local calendar label; UTC arithmetic on the label is
    // host-timezone-safe (same as clinic-time.js clinicDatePlusDays).
    const d = new Date(todayKey + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });
  const tomorrowKey = dayKeys[1];
  const dayMap = new Map(dayKeys.map((k) => [k, { date: k, label: dayLabel(k, todayKey, tomorrowKey), rows: [] }]));

  for (const appt of appts || []) {
    const dayKey = clinicDateKey(appt.start);
    if (!dayMap.has(dayKey)) continue;
    const patientRef = patientRefOf(appt);
    const patient = patientRef ? patients[patientRef] : null;
    const enc = encByKey.get((patientRef || '') + '|' + (appt.start || ''));
    const stateExt = enc && ext(enc, OPD_STATE);
    const state = (stateExt && stateExt.valueString) || APPT_STATUS_TO_STATE[appt.status] || 'DRAFT';
    const doctorParticipant = doctorParticipantOf(appt);
    const name = patient ? nameOf(patient) : (doctorParticipant ? '' : 'Patient');
    const mrn = patient && (patient.identifier || []).find((i) => i.system === MRN_SYS);
    dayMap.get(dayKey).rows.push({
      id: appt.id, appointmentId: appt.id, encounterId: enc ? enc.id : null,
      patientId: patient ? patient.id : (patientRef ? patientRef.split('/').pop() : null),
      time: fmtTime(appt.start), timeIso: appt.start,
      patientName: name, patientInitials: initials(name),
      mrn: mrn ? mrn.value : '',
      age: ageFromBirthDate(patient && patient.birthDate),
      sex: (patient && patient.gender) ? patient.gender[0].toUpperCase() : '',
      doctor: doctorParticipant ? doctorParticipant.actor.display || '' : '',
      doctorId: doctorParticipant ? doctorParticipant.actor.reference.replace(/^Practitioner\//, '') : '',
      department: (enc && enc.serviceProvider && enc.serviceProvider.display) || '',
      state, rawState: state,
    });
  }
  for (const day of dayMap.values()) day.rows.sort((a, b) => String(a.timeIso || '').localeCompare(String(b.timeIso || '')));
  const days = Array.from(dayMap.values());
  return { today: todayKey, days, totalAppointments: days.reduce((n, d) => n + d.rows.length, 0) };
}
